import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";

import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";

import { Delete } from "@mui/icons-material";
import moment from "moment";

export default function Javakregister() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  // ===================== STATES =====================
  const [userData, setUserData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [username, setUsername] = useState("");

  const [departments, setDepartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [javakitems, setJavakitems] = useState([]);

  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [size, setSize] = useState("");

  const [javakdate, setJavakdate] = useState(moment().format("YYYY-MM-DD"));
  const [javakpersonname, setJavakpersonname] = useState("");
  const [javaknote, setJavaknote] = useState("");

  // ===================== AUTH + PROPERTY =====================
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserData(user);

        // fetch propertyId from authroles
        const roleSnap = await getDoc(doc(db, "authroles", user.uid));
        if (roleSnap.exists()) {
          setPropertyId(roleSnap.data().propertyId);
        }

        // fetch username
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) {
          setUsername(userSnap.data().username || "");
        }
      }
    });

    return () => unsub();
  }, []);

  // ===================== FETCH DEPARTMENTS =====================
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "departments"),
      where("propertyId", "==", propertyId)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      setDepartments(snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })));
    });

    return () => unsub();
  }, [propertyId]);

  // ===================== FETCH PRODUCTS =====================
  useEffect(() => {
    if (!selectedDepartment || !propertyId) {
      setProducts([]);
      return;
    }

    const q = query(
      collection(db, "storeproducts"),
      where("departmentid", "==", selectedDepartment.id),
      where("propertyId", "==", propertyId)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      setProducts(snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })));
    });

    return () => unsub();
  }, [selectedDepartment, propertyId]);

  // ===================== ADD ITEM =====================
  const handleAddItem = () => {
    if (!selectedDepartment || !selectedProduct || !quantity) {
      Toast.fire({ icon: "error", title: "All fields required" });
      return;
    }

    const newItem = {
      department: selectedDepartment.department,
      departmentId: selectedDepartment.id,
      product: selectedProduct.product,
      productid: selectedProduct.id,
      quantity: parseFloat(quantity),
      size,
      username,
      propertyId,
    };

    setJavakitems([...javakitems, newItem]);

    setSelectedProduct(null);
    setQuantity("");
    setSize("");
  };

  // ===================== REMOVE LOCAL ITEM =====================
  const removeItem = (index) => {
    setJavakitems(javakitems.filter((_, i) => i !== index));
  };

  // ===================== DELETE COMPLETE JAVAK =====================
  const handleDeleteRegister = async (javakid) => {
    if (!window.confirm("Delete this register?")) return;

    const itemsQuery = query(
      collection(db, "javakitems"),
      where("javakid", "==", javakid)
    );

    const itemsSnap = await getDocs(itemsQuery);
    await Promise.all(itemsSnap.docs.map((doc) => deleteDoc(doc.ref)));

    await deleteDoc(doc(db, "javakregisters", javakid));

    Toast.fire({ icon: "success", title: "Deleted successfully" });
  };

  // ===================== SUBMIT =====================
  const submitData = async () => {
    if (!javakitems.length) {
      Toast.fire({ icon: "error", title: "Add at least one product" });
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "javakregisters"), {
        javakdate,
        javakpersonname,
        javaknote,
        propertyId,
        uid: userData.uid,
        createdAt: Timestamp.now(),
      });

      await Promise.all(
        javakitems.map((item) =>
          addDoc(collection(db, "javakitems"), {
            ...item,
            javakid: docRef.id,
            createdAt: Timestamp.now(),
          })
        )
      );

      Toast.fire({ icon: "success", title: "Saved Successfully" });

      setJavakitems([]);
      setSelectedDepartment(null);
      setSelectedProduct(null);
      setQuantity("");
      setSize("");
      setJavakpersonname("");
      setJavaknote("");
    } catch (err) {
      Toast.fire({ icon: "error", title: err.message });
    }
  };

  // ===================== UI =====================
  return (
    <Box  className="content-wrapper" p={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Javak Register
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Department</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell>
                    <Autocomplete
                      options={departments}
                      getOptionLabel={(o) => o.department || ""}
                      value={selectedDepartment}
                      onChange={(e, val) => setSelectedDepartment(val)}
                      renderInput={(params) => (
                        <TextField {...params} size="small" />
                      )}
                    />
                  </TableCell>

                  <TableCell>
                    <Autocomplete
                      options={products}
                      getOptionLabel={(o) => o.product || ""}
                      value={selectedProduct}
                      onChange={(e, val) => setSelectedProduct(val)}
                      disabled={!selectedDepartment}
                      renderInput={(params) => (
                        <TextField {...params} size="small" />
                      )}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                    />
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddItem}
                    >
                      Add
                    </Button>
                  </TableCell>
                </TableRow>

                {javakitems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.department}</TableCell>
                    <TableCell>{item.product}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.size}</TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => removeItem(index)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={2} mt={3}>
            <Grid item xs={4}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                size="small"
                value={javakdate}
                onChange={(e) => setJavakdate(e.target.value)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Person Name"
                fullWidth
                size="small"
                value={javakpersonname}
                onChange={(e) => setJavakpersonname(e.target.value)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Note"
                fullWidth
                size="small"
                value={javaknote}
                onChange={(e) => setJavaknote(e.target.value)}
              />
            </Grid>
          </Grid>

          <Box mt={3} textAlign="center">
            <Button variant="contained" onClick={submitData}>
              Submit
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}